import type { PaymentMode } from '@repo/desktop-bridge';
import { z } from 'zod';

import { isRepresentablePaise, paiseFromRupees, RUPEES_SHAPE } from '@/lib/money';

/**
 * What the customer handed over, and the change — on both counters.
 *
 * It was the shop's alone. The gate has it too since #269 ("tendered and
 * balance on the admission bill, as the shop screen already has"), and a rule
 * used by two forms lives in one place (`docs/ui-conventions.md`): the field,
 * the keys and the change are here, and each screen draws `<CashGiven>`
 * (`CashGiven.tsx`).
 *
 * **Never sent to the API, and nothing depends on it.** It reaches the paper —
 * the tax invoice and the admission bill print what was tendered and the
 * balance, as the park's own paper does — and nothing else. `Take payment` is
 * never blocked on it.
 */

/**
 * The notes somebody is most likely to be holding, in paise.
 *
 * India's living higher denominations. ₹200 and ₹100 are deliberately absent:
 * the next round hundred above the total covers both and is a better guess than
 * either, because it is derived from what is actually owed.
 */
const NOTES_PAISE: readonly number[] = [50000, 100000, 200000];

/** A whole hundred rupees, in paise — the unit the round-up snaps to. */
const HUNDRED_PAISE = 10000;

/**
 * How many keys the row holds.
 *
 * Three is what fitted beside the field when the sale was a 26rem column, and
 * three is still right on the foot-anchored strip: the row shares that strip
 * with the payment modes and the total, so the width it has is not the width of
 * the counter. More keys is a longer row of guesses, and the first one — the
 * next round hundred — is the one that gets pressed.
 */
const TENDER_KEYS = 3;

/**
 * What a customer plausibly hands over for this total.
 *
 * The next round hundred first — for ₹650 that is ₹700, which is what somebody
 * pays with a ₹500 and two ₹100s — then the notes above it. The exact amount is
 * deliberately not offered: exact money produces no change, and a cashier
 * reaching for a change calculator is not in that case.
 *
 * Integers throughout, per `lib/money.ts`. The one division is of a paise
 * integer by 100_00 and is immediately ceilinged, so nothing here can produce a
 * fraction of a paisa.
 */
export function tenderKeys(totalPaise: number): readonly number[] {
  if (totalPaise <= 0) return [];

  const rounded = Math.ceil(totalPaise / HUNDRED_PAISE) * HUNDRED_PAISE;

  const candidates = [rounded, ...NOTES_PAISE].filter((amount) => amount > totalPaise);

  return [...new Set(candidates)].sort((a, b) => a - b).slice(0, TENDER_KEYS);
}

/**
 * What the customer handed over, in rupees as typed — the schema fragment both
 * payment forms hold.
 *
 * In the schema rather than in a `useState` beside it because
 * `docs/ui-conventions.md` allows exactly one way to build a form, and a field a
 * person types into is a field the form owns. What it validates is only the
 * shape: an amount that is not an amount would silently compute change off
 * `NaN`.
 */
export const cashTenderedField = z
  .string()
  .trim()
  .refine((value) => value === '' || RUPEES_SHAPE.test(value), {
    message: 'Enter an amount, like 500 or 500.50.',
  })
  .optional();

/**
 * What the customer handed over, in paise, or `null` while that is not yet a
 * number.
 *
 * `null` covers a half-typed amount as much as a malformed one — somebody on
 * their way to `1000` passes through `1`, and a change figure that counts down
 * as they type is a number nobody should read.
 */
export function tenderedPaiseFrom(typed: string | undefined): number | null {
  const trimmed = typed?.trim() ?? '';

  if (trimmed === '' || !RUPEES_SHAPE.test(trimmed)) return null;

  const paise = paiseFromRupees(trimmed);

  return isRepresentablePaise(paise) ? paise : null;
}

/**
 * The tendered amount as the till is asked to print it: on cash, and only where
 * one was typed. Spread into the request; never sent any further than the paper.
 */
export function cashTenderedOnRequest(
  mode: PaymentMode,
  typed: string | undefined,
): { cashTenderedPaise?: number } {
  const paise = mode === 'CASH' ? tenderedPaiseFrom(typed) : null;

  return paise === null ? {} : { cashTenderedPaise: paise };
}
